import { Wind } from "lucide-react";

const AQI_LEVELS = [
  { label: "—", color: "oklch(0.5 0 0)" },
  { label: "Good", color: "oklch(0.7 0.18 145)" },
  { label: "Fair", color: "oklch(0.8 0.18 110)" },
  { label: "Moderate", color: "oklch(0.85 0.18 90)" },
  { label: "Poor", color: "oklch(0.7 0.2 50)" },
  { label: "Very Poor", color: "oklch(0.6 0.22 25)" },
];

interface AqiGaugeProps {
  aqi: { list: { main: { aqi: number }; components: Record<string, number> }[] } | null;
}

export function AqiGauge({ aqi }: AqiGaugeProps) {
  if (!aqi?.list?.[0]) return null;

  const level = aqi.list[0].main.aqi;
  const comps = aqi.list[0].components;
  const { label, color } = AQI_LEVELS[level] || AQI_LEVELS[0];
  const progress = level / 5;

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold flex items-center gap-2">
        <Wind className="h-4 w-4 text-primary" />
        Air Quality
      </h3>

      <div className="flex items-center gap-4 mb-4">
        <svg viewBox="0 0 80 80" className="w-20 h-20 flex-shrink-0">
          <circle cx="40" cy="40" r="32" fill="none" stroke="oklch(1 0 0 / 0.08)" strokeWidth="6" />
          <circle
            cx="40"
            cy="40"
            r="32"
            fill="none"
            stroke={color}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${progress * 201} 201`}
            transform="rotate(-90 40 40)"
          />
          <text x="40" y="44" textAnchor="middle" fontSize="22" className="fill-foreground font-bold">
            {level}
          </text>
          <text x="40" y="55" textAnchor="middle" fontSize="7" className="fill-muted-foreground">
            of 5
          </text>
        </svg>

        <div>
          <p className="text-lg font-bold" style={{ color }}>{label}</p>
          <p className="text-xs text-muted-foreground">European AQI</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <Pollutant label="PM2.5" value={comps.pm2_5} unit="µg/m³" />
        <Pollutant label="PM10" value={comps.pm10} unit="µg/m³" />
        <Pollutant label="O₃" value={comps.o3} unit="µg/m³" />
        <Pollutant label="NO₂" value={comps.no2} unit="µg/m³" />
        <Pollutant label="SO₂" value={comps.so2} unit="µg/m³" />
        <Pollutant label="CO" value={comps.co} unit="µg/m³" />
      </div>
    </div>
  );
}

function Pollutant({ label, value, unit }: { label: string; value: number; unit: string }) {
  return (
    <div className="rounded-md bg-muted/50 px-2 py-1.5 border border-border/50">
      <p className="text-muted-foreground text-[10px]">{label}</p>
      <p className="font-semibold text-xs">{value?.toFixed(1)}</p>
      <p className="text-[9px] text-muted-foreground">{unit}</p>
    </div>
  );
}
